/**
 * API Service for Planning Poker backend
 * Handles all communication with the Lambda backend
 */

const PP_API_URL = import.meta.env.VITE_PP_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:3001';
const API_URL = PP_API_URL.endsWith('/') ? PP_API_URL.slice(0, -1) : PP_API_URL;
const BASE_PATH = '/api/planning-poker';

function normalizeRoomId(roomId: string): string {
  return roomId.trim().toUpperCase();
}

export interface CreateRoomResponse {
  roomId: string;
  adminToken: string;
}

export interface JoinRoomResponse {
  success: boolean;
  room: Room;
  userToken: string;
}

export interface CreateRoomRequest {
  name: string;
  adminName: string;
  email: string;
}

export interface JoinRoomRequest {
  userName: string;
  email: string;
}

export interface AddSituationRequest {
  description: string;
  adminToken: string;
}

export interface StartVotingRequest {
  situationId: string;
}

export interface SubmitVoteRequest {
  situationId: string;
  playerId: string;
  playerName: string;
  cardId: string;
}

export interface Situation {
  id: string;
  description: string;
  createdAt: number;
  finalChoice?: string;
  voteResults?: Record<string, number>;
  voteDetails?: Record<string, Array<{ userId: string; userName?: string }>>;
}

export interface Vote {
  userId: string;
  userName?: string;
  level: number;
  votedAt: number;
}

export interface VotingSession {
  situationId: string;
  startedAt: number;
  endsAt: number;
  votes: Vote[];
  isActive: boolean;
}

export interface Participant {
  userId: string;
  name: string;
  isAdmin: boolean;
  joinedAt: number;
}

export interface Room {
  roomId: string;
  id?: string;
  name: string;
  createdAt: number;
  createdBy: string;
  participants: Participant[];
  situations: Situation[];
  currentVotingSession?: VotingSession;
  ttl?: number;
}

/**
 * Create a new room
 */
export async function createRoom(request: CreateRoomRequest): Promise<CreateRoomResponse> {
  try {
    const response = await fetch(`${API_URL}${BASE_PATH}/rooms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const text = await response.text();
    let data: unknown;

    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error('Invalid JSON response:', text);
      throw new Error(`Server returned invalid response: ${text.substring(0, 100)}`);
    }

    if (!response.ok) {
      throw new Error((data as { error?: string }).error || `Server error: ${response.status}`);
    }

    return data as CreateRoomResponse;
  } catch (err) {
    console.error('Create room error:', err);
    throw err;
  }
}

/**
 * Get room by ID
 */
export async function getRoom(roomId: string): Promise<Room> {
  try {
    const normalizedRoomId = normalizeRoomId(roomId);
    const response = await fetch(`${API_URL}${BASE_PATH}/rooms/${normalizedRoomId}`, {
      method: 'GET',
    });

    const text = await response.text();
    let data: unknown;

    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error('Invalid JSON response:', text);
      throw new Error(`Server returned invalid response: ${text.substring(0, 100)}`);
    }

    if (!response.ok) {
      throw new Error((data as { error?: string }).error || `Server error: ${response.status}`);
    }

    return data as Room;
  } catch (err) {
    console.error('Get room error:', err);
    throw err;
  }
}

/**
 * Join an existing room
 */
export async function joinRoom(
  roomId: string,
  request: JoinRoomRequest
): Promise<JoinRoomResponse> {
  try {
    const normalizedRoomId = normalizeRoomId(roomId);
    const response = await fetch(`${API_URL}${BASE_PATH}/rooms/${normalizedRoomId}/join`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const text = await response.text();
    let data: unknown;

    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error('Invalid JSON response:', text);
      throw new Error(`Server returned invalid response: ${text.substring(0, 100)}`);
    }

    if (!response.ok) {
      throw new Error((data as { error?: string }).error || `Server error: ${response.status}`);
    }

    return data as JoinRoomResponse;
  } catch (err) {
    console.error('Join room error:', err);
    throw err;
  }
}

/**
 * Add a new user story
 */
export async function addSituation(
  roomId: string,
  request: AddSituationRequest
): Promise<Room> {
  try {
    const normalizedRoomId = normalizeRoomId(roomId);
    const response = await fetch(`${API_URL}${BASE_PATH}/rooms/${normalizedRoomId}/situations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const text = await response.text();
    let data: unknown;

    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error('Invalid JSON response:', text);
      throw new Error(`Server returned invalid response: ${text.substring(0, 100)}`);
    }

    if (!response.ok) {
      throw new Error((data as { error?: string }).error || `Server error: ${response.status}`);
    }

    return data as Room;
  } catch (err) {
    console.error('Add situation error:', err);
    throw err;
  }
}

/**
 * Remove a user story
 */
export async function removeSituation(
  roomId: string,
  situationId: string
): Promise<Room> {
  const normalizedRoomId = normalizeRoomId(roomId);
  const response = await fetch(
    `${API_URL}${BASE_PATH}/rooms/${normalizedRoomId}/situations/${situationId}`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove situation');
  }

  return response.json();
}

/**
 * Start voting for a user story
 */
export async function startVoting(
  roomId: string,
  request: StartVotingRequest
): Promise<Room> {
  try {
    const normalizedRoomId = normalizeRoomId(roomId);
    const response = await fetch(
      `${API_URL}${BASE_PATH}/rooms/${normalizedRoomId}/situations/${request.situationId}/vote`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const text = await response.text();
    let data: unknown;

    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error('Invalid JSON response:', text);
      throw new Error(`Server returned invalid response: ${text.substring(0, 100)}`);
    }

    if (!response.ok) {
      throw new Error((data as { error?: string }).error || `Server error: ${response.status}`);
    }

    return data as Room;
  } catch (err) {
    console.error('Start voting error:', err);
    throw err;
  }
}

/**
 * Submit a vote for a user story
 */
export async function submitVote(
  roomId: string,
  request: SubmitVoteRequest
): Promise<Room> {
  try {
    const normalizedRoomId = normalizeRoomId(roomId);
    const response = await fetch(
      `${API_URL}${BASE_PATH}/rooms/${normalizedRoomId}/situations/${request.situationId}/votes`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerId: request.playerId,
          playerName: request.playerName,
          cardId: request.cardId,
        }),
      }
    );

    const text = await response.text();
    let data: unknown;

    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error('Invalid JSON response:', text);
      throw new Error(`Server returned invalid response: ${text.substring(0, 100)}`);
    }

    if (!response.ok) {
      throw new Error((data as { error?: string }).error || `Server error: ${response.status}`);
    }

    return data as Room;
  } catch (err) {
    console.error('Submit vote error:', err);
    throw err;
  }
}

/**
 * Close voting results and set final choice
 */
export async function closeVotingResults(
  roomId: string,
  situationId: string,
  finalChoice: string
): Promise<Room> {
  try {
    const normalizedRoomId = normalizeRoomId(roomId);
    const response = await fetch(
      `${API_URL}${BASE_PATH}/rooms/${normalizedRoomId}/situations/${situationId}/close-results`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ finalChoice }),
      }
    );

    const text = await response.text();
    let data: unknown;

    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error('Invalid JSON response:', text);
      throw new Error(`Server returned invalid response: ${text.substring(0, 100)}`);
    }

    if (!response.ok) {
      throw new Error((data as { error?: string }).error || `Server error: ${response.status}`);
    }

    return data as Room;
  } catch (err) {
    console.error('Close voting results error:', err);
    throw err;
  }
}

/**
 * Reset voting for a user story
 */
export async function voteAgain(
  roomId: string,
  situationId: string
): Promise<Room> {
  try {
    const normalizedRoomId = normalizeRoomId(roomId);
    const response = await fetch(
      `${API_URL}${BASE_PATH}/rooms/${normalizedRoomId}/situations/${situationId}/vote-again`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const text = await response.text();
    let data: unknown;

    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error('Invalid JSON response:', text);
      throw new Error(`Server returned invalid response: ${text.substring(0, 100)}`);
    }

    if (!response.ok) {
      throw new Error((data as { error?: string }).error || `Server error: ${response.status}`);
    }

    return data as Room;
  } catch (err) {
    console.error('Vote again error:', err);
    throw err;
  }
}

/**
 * Delete a user story from a room
 */
export async function deleteSituation(
  roomId: string,
  situationId: string,
  adminToken: string
): Promise<void> {
  try {
    const normalizedRoomId = normalizeRoomId(roomId);
    const response = await fetch(
      `${API_URL}${BASE_PATH}/rooms/${normalizedRoomId}/situations/${situationId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ adminToken }),
      }
    );

    if (!response.ok) {
      const text = await response.text();
      let data: unknown;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error(`Server error: ${response.status}`);
      }
      throw new Error((data as { error?: string }).error || `Server error: ${response.status}`);
    }
  } catch (err) {
    console.error('Delete situation error:', err);
    throw err;
  }
}
